import React, { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { CONTROL, PRES, TYPE } from './chrome';
import { childDeckCategories, rootDeckCategories, UNCATEGORIZED_DECK_FILTER } from './deckCategoryTree';
import { COPY } from './presentationCopy';
import { PresentationDeckCategory } from './types';
import { focusVisibleSx } from './ui/focus';

interface DeckCategoryNavProps {
  categories: PresentationDeckCategory[];
  selectedId: string;
  canManage: boolean;
  deleteConfirm?: string;
  onSelect: (id: string) => void;
  onCreate: (name: string, parentCategory?: string | null) => Promise<void>;
  onRename: (id: string, name: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

function TreeButton({
  label,
  selected,
  indent,
  onClick,
  action,
}: {
  label: string;
  selected: boolean;
  indent?: boolean;
  onClick: () => void;
  action?: React.ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', pl: indent ? 2 : 0 }}>
      <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          flex: 1,
          textAlign: 'left',
          border: 0,
          bgcolor: selected ? PRES.surfaceSunken : 'transparent',
          color: PRES.textPrimary,
          fontFamily: TYPE.ui,
          ...TYPE.body,
          px: 1,
          py: 0.65,
          borderRadius: `${CONTROL.radius}px`,
          cursor: 'pointer',
          ...focusVisibleSx,
        }}
      >
        {label}
      </Box>
      {action}
    </Box>
  );
}

const DeckCategoryNav: React.FC<DeckCategoryNavProps> = ({
  categories,
  selectedId,
  canManage,
  deleteConfirm,
  onSelect,
  onCreate,
  onRename,
  onDelete,
}) => {
  const [dialog, setDialog] = useState<{ mode: 'create' | 'rename'; parentId?: string | null; id?: string; name: string } | null>(null);
  const [menu, setMenu] = useState<{ id: string; anchor: HTMLElement } | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const submitDialog = async () => {
    if (!dialog?.name.trim()) return;
    if (dialog.mode === 'create') await onCreate(dialog.name.trim(), dialog.parentId);
    if (dialog.mode === 'rename' && dialog.id) await onRename(dialog.id, dialog.name.trim());
    setDialog(null);
  };

  const categoryAction = (category: PresentationDeckCategory) => canManage ? (
    <IconButton
      size="small"
      aria-label={COPY.moreActions}
      onClick={(event) => {
        event.stopPropagation();
        setMenu({ id: category._id, anchor: event.currentTarget });
      }}
      sx={{ color: PRES.textSecondary, ...focusVisibleSx }}
    >
      <MoreHorizIcon fontSize="small" />
    </IconButton>
  ) : null;

  return (
    <Box sx={{
      width: 240,
      flexShrink: 0,
      borderRight: `1px solid ${PRES.borderSubtle}`,
      bgcolor: PRES.surface,
      p: 1.5,
      overflow: 'auto',
    }}>
      <TreeButton label={COPY.filterAll} selected={selectedId === ''} onClick={() => onSelect('')} />
      <TreeButton
        label={COPY.uncategorized}
        selected={selectedId === UNCATEGORIZED_DECK_FILTER}
        onClick={() => onSelect(UNCATEGORIZED_DECK_FILTER)}
      />
      {rootDeckCategories(categories).map((root) => (
        <Box key={root._id} sx={{ mt: 0.5 }}>
          <TreeButton
            label={root.name}
            selected={selectedId === root._id}
            onClick={() => onSelect(root._id)}
            action={categoryAction(root)}
          />
          {childDeckCategories(categories, root._id).map((child) => (
            <TreeButton
              key={child._id}
              label={child.name}
              selected={selectedId === child._id}
              indent
              onClick={() => onSelect(child._id)}
              action={categoryAction(child)}
            />
          ))}
        </Box>
      ))}
      {canManage && (
        <Button
          startIcon={<AddIcon />}
          onClick={() => setDialog({ mode: 'create', parentId: null, name: '' })}
          sx={{ mt: 1.5, textTransform: 'none', color: PRES.interactive }}
        >
          {COPY.newCategory}
        </Button>
      )}
      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => {
          const current = categories.find((category) => category._id === menu?.id);
          setDialog({ mode: 'rename', id: menu?.id, name: current?.name || '' });
          setMenu(null);
        }}>{COPY.renameCategory}</MenuItem>
        {menu && !parentDeckCategoryIdSafe(categories, menu.id) && (
          <MenuItem onClick={() => {
            setDialog({ mode: 'create', parentId: menu.id, name: '' });
            setMenu(null);
          }}>{COPY.newSubcategory}</MenuItem>
        )}
        <MenuItem onClick={() => {
          setConfirmId(menu?.id || null);
          setMenu(null);
        }}>{COPY.deleteCategory}</MenuItem>
      </Menu>
      <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>{dialog?.mode === 'rename' ? COPY.renameCategory : dialog?.parentId ? COPY.newSubcategory : COPY.newCategory}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={dialog?.name || ''}
            placeholder={COPY.categoryNamePlaceholder}
            onChange={(event) => setDialog(dialog ? { ...dialog, name: event.target.value } : dialog)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
          <Button disabled={!dialog?.name.trim()} onClick={() => void submitDialog()} sx={{ textTransform: 'none' }}>{COPY.confirm}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(confirmId)} onClose={() => setConfirmId(null)}>
        <DialogTitle>{COPY.deleteCategory}</DialogTitle>
        <DialogContent>{deleteConfirm || COPY.confirmDeleteCategory}</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmId(null)} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
          <Button color="error" onClick={async () => {
            if (confirmId) await onDelete(confirmId);
            setConfirmId(null);
          }} sx={{ textTransform: 'none' }}>{COPY.delete}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function parentDeckCategoryIdSafe(categories: PresentationDeckCategory[], id: string): string | null {
  const category = categories.find((entry) => entry._id === id);
  if (!category) return null;
  const parent = category.parentCategory;
  return parent ? String(parent) : null;
}

export default DeckCategoryNav;
